import { useEffect, useRef, useState } from "react";

function checkIfInViewport(boundingRect) {
    const { top, left } = boundingRect;

    return (
        top < (window.innerHeight || document.documentElement.clientHeight) &&
        left < (window.innerWidth || document.documentElement.clientWidth)
    );
}

export function useIntersectionObserver(
    { root = null, rootMargin, threshold = 0, once = false } = {},
    targetRef = null
) {
    const [isVisible, setIsVisible] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [intersectionRef, setIntersectionRef] = useState(targetRef);
    let observer = useRef();

    useEffect(() => {
        const targetNode = intersectionRef?.current;
        if (targetNode) {
            observer.current = new window.IntersectionObserver(
                ([entry], thisObserver) => {
                    let visible = entry.isIntersecting;

                    if (!visible && entry.boundingClientRect.top === 0) {
                        // edge case when IntersectionObserver does not see on load an element in viewport
                        // that has been mounted using renderReactComponentInDocument()
                        visible = checkIfInViewport(targetNode?.getBoundingClientRect());
                    }

                    setIsVisible(visible);
                    setIsReady(true);

                    if (visible && once) {
                        thisObserver.disconnect();
                    }
                },
                { root, rootMargin, threshold }
            );
            observer.current?.observe?.(targetNode);
        }

        return () => {
            observer.current?.disconnect?.();
        };
    }, [intersectionRef, once, root, rootMargin, threshold]);

    return [isVisible, setIntersectionRef, isReady];
}
